import { Button } from "@/components/ui/button.tsx";
import {
  ContactAutocompleteDropdown,
  type ContactAutocompleteDropdownRef,
  type ContactDropdownPosition,
} from "@/components/ui/contact-autocomplete.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { cn } from "@/lib/utils.ts";
import type { Contact, Handle } from "@/types/contact.ts";
import { CalendarPlus, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { UI_CONSTANTS } from "./constants.ts";
import { useGateValue } from "@statsig/react-bindings";

const DURATION_OPTIONS = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "60", label: "1 hour" },
  { value: "90", label: "1.5 hours" },
  { value: "120", label: "2 hours" },
];

const DURATION_LABELS: Record<string, string> = {
  "15": "15 minutes",
  "30": "30 minutes",
  "45": "45 minutes",
  "60": "1 hour",
  "90": "1.5 hours",
  "120": "2 hours",
};

interface Participant {
  id: string;
  name: string;
  email?: string;
}

interface ParticipantInputProps {
  participants: Participant[];
  onParticipantsChange: (participants: Participant[]) => void;
}

const ParticipantInput = ({ participants, onParticipantsChange }: ParticipantInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<ContactAutocompleteDropdownRef>(null);
  const [query, setQuery] = useState("");
  const [dropdownPos, setDropdownPos] = useState<ContactDropdownPosition | null>(null);

  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setQuery(value);
      if (value) {
        updateDropdownPosition();
      } else {
        setDropdownPos(null);
      }
    },
    [updateDropdownPosition],
  );

  const addParticipant = useCallback(
    (participant: Participant) => {
      if (participants.some((p) => p.id === participant.id)) {
        setQuery("");
        setDropdownPos(null);
        return;
      }
      onParticipantsChange([...participants, participant]);
      setQuery("");
      setDropdownPos(null);
      inputRef.current?.focus();
    },
    [participants, onParticipantsChange],
  );

  const handleContactSelect = useCallback(
    (contact: Contact) => {
      const emailHandle = contact.handles?.find((h) => h.type === "emailAddress");
      addParticipant({
        id: contact.id.toString(),
        name: contact.name || emailHandle?.value || "Unknown",
        email: emailHandle?.value,
      });
    },
    [addParticipant],
  );

  const handleHandleSelect = useCallback(
    (contact: Contact, handle: Handle) => {
      addParticipant({
        id: contact.id.toString(),
        name: contact.name || handle.value || "Unknown",
        email: handle.value,
      });
    },
    [addParticipant],
  );

  const handleEmailSelect = useCallback(
    (email: string) => {
      addParticipant({
        id: email,
        name: email,
        email,
      });
    },
    [addParticipant],
  );

  const removeParticipant = useCallback(
    (id: string) => {
      onParticipantsChange(participants.filter((p) => p.id !== id));
    },
    [participants, onParticipantsChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (query && dropdownRef.current) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          dropdownRef.current.handleArrowDown();
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          dropdownRef.current.handleArrowUp();
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          dropdownRef.current.handleEnter();
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setQuery("");
          setDropdownPos(null);
          return;
        }
      }
      if (e.key === "Backspace" && !query && participants.length > 0) {
        removeParticipant(participants[participants.length - 1].id);
      }
    },
    [query, participants, removeParticipant],
  );

  return (
    <div className="relative">
      <div
        className={cn(
          "flex flex-wrap items-center gap-1.5 min-h-10 w-full rounded-full border border-input bg-background px-3 py-2",
          "focus-within:outline-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
        )}
        onClick={() => inputRef.current?.focus()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            inputRef.current?.focus();
          }
        }}
      >
        {participants.map((p) => (
          <span
            key={p.id}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-sm"
          >
            {p.name}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeParticipant(p.id);
              }}
              className="hover:bg-primary/20 rounded-full p-0.5"
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm placeholder:text-muted-foreground"
          placeholder={participants.length === 0 ? "Type a name or email..." : ""}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />
      </div>
      {query && dropdownPos && (
        <ContactAutocompleteDropdown
          ref={dropdownRef}
          query={query}
          position={dropdownPos}
          onSelect={handleContactSelect}
          onSelectHandle={handleHandleSelect}
          onSelectEmail={handleEmailSelect}
          filterHandleTypes={["emailAddress"]}
        />
      )}
    </div>
  );
};

export interface QuickScheduleProps {
  /**
   * Called when the user submits the form with the formatted message text.
   * The parent is responsible for creating the thread and sending the message.
   */
  onSubmit: (messageText: string) => Promise<void>;
  /**
   * The control that opens the form. Given by a host with somewhere specific to
   * put it — the composer's tools menu wants a labelled row rather than the
   * naked glyph this renders on a toolbar. It receives the opener; absent, the
   * glyph is the trigger.
   */
  renderTrigger?: (open: () => void) => React.ReactNode;
}

/**
 * Self-contained quick schedule component with button and dialog.
 * Manages its own open/close state and form state.
 */
export const QuickSchedule = ({ onSubmit, renderTrigger }: QuickScheduleProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [duration, setDuration] = useState("30");
  const [location, setLocation] = useState("");
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [dateTime, setDateTime] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const quickScheduleEnabled = useGateValue("quick_schedule_enabled");

  // Handle open/close with animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const timer = setTimeout(() => {
        setIsAnimating(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const resetForm = useCallback(() => {
    setSubject("");
    setDuration("30");
    setLocation("");
    setParticipants([]);
    setDateTime("");
  }, []);

  const handleSubmit = useCallback(async () => {
    const durationLabel = DURATION_LABELS[duration] || `${duration} minutes`;

    const parts = [`Schedule a meeting about "${subject}"`];
    parts.push(`Duration: ${durationLabel}`);
    if (participants.length > 0) {
      parts.push(`Participants: ${participants.map((p) => p.email || p.name).join(", ")}`);
    }
    if (dateTime) {
      const date = new Date(dateTime);
      parts.push(`Preferred time: ${date.toLocaleString()}`);
    }
    if (location) {
      parts.push(`Location: ${location}`);
    }

    const messageText = parts.join("\n");

    await onSubmit(messageText);
    resetForm();
    setIsOpen(false);
  }, [subject, duration, location, participants, dateTime, onSubmit, resetForm]);

  const handleCancel = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      // Blur active element to ensure proper focus state restoration
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      resetForm();
      setIsOpen(false);
    },
    [resetForm],
  );

  const isSubmitDisabled = !subject.trim();

  if (!quickScheduleEnabled) {
    return null;
  }

  return (
    <>
      {renderTrigger ? (
        renderTrigger(() => setIsOpen(true))
      ) : (
        /* Trigger Button */
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(true)}
              aria-label="Schedule a meeting"
              aria-pressed={isOpen}
              className={cn(
                "p-2",
                UI_CONSTANTS.BUTTON_SIZE,
                isOpen
                  ? "bg-muted-foreground/20 text-foreground"
                  : "bg-secondary hover:bg-secondary/80",
              )}
              data-statsig-name="omnibox-quick-schedule-open"
            >
              <CalendarPlus className={UI_CONSTANTS.ICON_SIZE} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Schedule a new meeting or event</TooltipContent>
        </Tooltip>
      )}

      {/* Dialog */}
      {shouldRender && (
        <>
          {/* Backdrop */}
          <div
            className={cn(
              "fixed inset-0 z-40 transition-opacity duration-200",
              isAnimating ? "opacity-100" : "opacity-0",
            )}
            onClick={handleCancel}
            onKeyDown={(e) => {
              if (e.key === "Escape") handleCancel();
            }}
          />

          {/* Dialog content */}
          <div
            className={cn(
              "absolute z-50 w-full border bg-popover text-popover-foreground p-6 shadow-lg rounded-2xl",
              "left-0 right-0 bottom-full mb-2",
              "transition-all duration-200 ease-out",
              isAnimating ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
            )}
          >
            <div className="flex flex-col space-y-1.5 text-left">
              <h2 className="text-lg font-semibold leading-none tracking-tight">
                Schedule a meeting
              </h2>
              <p className="text-sm text-muted-foreground">
                Provide the meeting details and I'll help coordinate with participants.
              </p>
            </div>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="schedule-subject">Subject *</Label>
                <Input
                  id="schedule-subject"
                  placeholder="What is this meeting about?"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="schedule-participants">Participants</Label>
                <ParticipantInput
                  participants={participants}
                  onParticipantsChange={setParticipants}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="schedule-duration">Duration</Label>
                  <Select value={duration} onValueChange={setDuration}>
                    <SelectTrigger id="schedule-duration">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      {DURATION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="schedule-datetime">Preferred date/time</Label>
                  <Input
                    id="schedule-datetime"
                    type="datetime-local"
                    value={dateTime}
                    onChange={(e) => setDateTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="schedule-location">Location</Label>
                <Input
                  id="schedule-location"
                  placeholder="e.g., Zoom, Coffee Shop, Office"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-2 sm:gap-0">
              <Button
                variant="secondary"
                onClick={handleCancel}
                data-statsig-name="omnibox-quick-schedule-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                data-statsig-name="omnibox-quick-schedule-submit"
              >
                Start scheduling
              </Button>
            </div>

            <button
              type="button"
              onClick={handleCancel}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </>
      )}
    </>
  );
};
